import {
  GraphQLInputObjectType,
  GraphQLString,
  GraphQLList,
  GraphQLEnumType,
  GraphQLNonNull,
  GraphQLID,
  GraphQLBoolean
} from 'graphql'
import { dateTimeType } from './date-time-type'

type VulnerabilityDateField = 'FIRST' | 'LAST'

interface VulnerabilityDateFilter {
  readonly start: Date
  readonly end: Date
  readonly field: VulnerabilityDateField
}

type VulnerabilityGroupedBy = 'SINK'

export interface VulnerabilityFilterArg {
  agentSessionId?: string[]
  tags?: string[]
  severities?: string[]
  statuses?: string[]
  bugtrackerStatuses?: string[]
  substatus?: string[]
  types?: string[]
  servers?: string[]
  environments?: string[]
  urls?: string[]
  sinks?: string[]
  groupedBy?: VulnerabilityGroupedBy
  applications?: string[]
  modules?: string[]
  securityStandards?: string[]
  buildNumbers?: string[]
  routes?: string[]
  dateFilter?: VulnerabilityDateFilter
  filterText?: string
  quickFilter?: string
  sessionMetadata?: Array<{
    fieldID: string
    values: string[]
  }>
  technologies?: string[]
  applicationTags?: string[]
  applicationMetadata?: Array<{
    fieldID: string
    values: string[]
  }>
  applicationImportances?: string[]
  languages?: string[]
  licensedOnly?: boolean
  protectStatuses?: string[]
}

const vulnerabilityDateFieldType = new GraphQLEnumType({
  name: 'VulnerabilityDateField',
  description:
    'Date fields of a vulnerability that can be used to filter by a date range',
  values: {
    FIRST: {
      description: 'First time seen'
    },
    LAST: {
      description: 'Last time seen'
    }
  }
})

const vulnerabilityDateFilterType = new GraphQLInputObjectType({
  name: 'VulnerabilityDateFilter',
  description: 'Options for filtering vulnerabilities using a date range',
  fields: () => ({
    start: {
      description: 'Filter with a date range starting from this date',
      type: new GraphQLNonNull(dateTimeType)
    },
    end: {
      description: 'Filter with a date range ending at this date',
      type: new GraphQLNonNull(dateTimeType)
    },
    field: {
      description: 'The date field that must fall in the specified date range',
      type: new GraphQLNonNull(vulnerabilityDateFieldType)
    }
  })
})

const groupedByType = new GraphQLEnumType({
  name: 'VulnerabilityGroupedByFilter',
  description: 'Options for grouping vulnerabilities by these values',
  values: {
    SINK: {
      description: 'Vulnerability sink'
    }
  }
})

const filterList = new GraphQLList(GraphQLString)

export const vulnerabilityFilterArgType = new GraphQLInputObjectType({
  name: 'VulnerabilityFilterArg',
  description:
    'A collection of criteria categories and values to filter vulnerabilities by. Each criteria category consists of a list of strings. Categories of criteria are applied with an AND relationship. Within each category, the individual criteria values are applied with an OR relationship',
  fields: () => ({
    agentSessionId: {
      description: 'Filters vulnerabilities by session',
      type: filterList
    },
    tags: {
      description: 'Filters vulnerabilities by tags',
      type: filterList
    },
    severities: {
      description: 'Filters vulnerabilities by severities',
      type: filterList
    },
    statuses: {
      description: 'Filters vulnerabilities by workflow statuses',
      type: filterList
    },
    bugtrackerStatuses: {
      description: 'Filters vulnerabilities by tracked or untracked',
      type: filterList
    },
    substatus: {
      description: 'Filter vulnerabilities by substatus',
      type: filterList
    },
    types: {
      description: 'Filters vulnerabilities by vulnerability types',
      type: filterList
    },
    servers: {
      description: 'Filters vulnerabilities by server IDs',
      type: filterList
    },
    environments: {
      description: 'Filters vulnerabilities by server environments',
      type: filterList
    },
    urls: {
      description: 'Filters vulnerabilities by URL',
      type: filterList
    },
    sinks: {
      description: 'Filters vulnerabilities by sink',
      type: filterList
    },
    groupedBy: {
      description: 'Groups vulnerabilities by value',
      type: groupedByType
    },
    applications: {
      description: 'Filters vulnerabilities by application IDs',
      type: filterList
    },
    modules: {
      description:
        'Filters vulnerabilities by application module IDs. This is only applicable when there is a single option in the applications filter',
      type: filterList
    },
    securityStandards: {
      description:
        'Filters vulnerabilities in violation of a security standard or compliance policy',
      type: filterList
    },
    buildNumbers: {
      description: 'Filters vulnerabilities by application build number',
      type: filterList
    },
    routes: {
      description: 'Filters vulnerabilities by related route',
      type: filterList
    },
    dateFilter: {
      description: 'Filter vulnerabilities by a date range',
      type: vulnerabilityDateFilterType
    },
    filterText: {
      description:
        'Filter vulnerabilities that contain the specified text string',
      type: GraphQLString
    },
    quickFilter: {
      description: 'An optional predefined filter',
      type: GraphQLString
    },
    sessionMetadata: {
      description: 'Filter by session metadata fields',
      type: new GraphQLList(
        new GraphQLInputObjectType({
          name: 'SessionMetadataFilter',
          description: 'Filter by a session metadata field',
          fields: () => ({
            fieldID: {
              description: 'ID of the session metadata field',
              type: new GraphQLNonNull(GraphQLID)
            },
            values: {
              description: 'Session metadata field value IDs to filter on',
              type: new GraphQLNonNull(
                new GraphQLList(new GraphQLNonNull(GraphQLID))
              )
            }
          })
        })
      )
    },
    technologies: {
      description: 'Filters vulnerabilities by application technology',
      type: filterList
    },
    applicationTags: {
      description: 'Filters vulnerabilities by application tags',
      type: filterList
    },
    applicationMetadata: {
      description: 'Filter by custom metadata fields',
      type: new GraphQLList(
        new GraphQLInputObjectType({
          name: 'ApplicationMetadataFilter',
          description: 'Filter by a session metadata field',
          fields: () => ({
            fieldID: {
              description: 'ID of the session metadata field',
              type: new GraphQLNonNull(GraphQLID)
            },
            values: {
              description: 'Application metadata field value IDs to filter on',
              type: new GraphQLNonNull(
                new GraphQLList(new GraphQLNonNull(GraphQLID))
              )
            }
          })
        })
      )
    },
    applicationImportances: {
      description: 'Filters vulnerabilities by application importance',
      type: filterList
    },
    languages: {
      description: 'Filters vulnerabilities by language',
      type: filterList
    },
    licensedOnly: {
      description:
        'Whether to only include vulnerabilities for licensed applications',
      type: GraphQLBoolean
    },
    protectStatuses: {
      description: 'Protect statuses within selected assess environment',
      type: filterList
    }
  })
})
